import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import {
    type ProductMetricsDto,
    getReportSavingsLastRequestParams,
} from 'entities/ReportSavingsSummary';
import { $api } from 'shared/api/api';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';
import { formatReportPercent } from 'shared/lib/reportFormatters';

interface ProductsSkuRequestBody {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
    orderBy?: string;
    page?: number;
    pageSize?: number;
}

interface ProductOrderSummaryItem {
    submittedAt: string;
    order: {
        orderId: string;
        orderNumber: string;
    };
    propertyName: string;
    suppliers: string[];
    quantity: number;
    totalPaid: number;
    baselinePrice: number;
    totalSaved: number;
    percentSaved: number;
    totalMissed?: number;
    missedPercent?: number;
    totalOverspend?: number;
    overspendPercent?: number;
}

interface ProductOrdersBySkuResponse {
    count: number;
    page: number;
    totalPages: number;
    items: ProductOrderSummaryItem[];
}

interface UseReportsPageSavingsSummaryProductResult {
    isSavingsSummaryVisible: boolean;
    sku: string | undefined;
    product: ProductMetricsDto | null;
    fromMissedSavings: boolean;
    loading: boolean;
    error: string | null;
    ordersData: ProductOrdersBySkuResponse | null;
    dateFrom: Date;
    dateTo: Date;
    datePreset?: string;
    totalSaved: number;
    totalSpent: number;
    baselineTotal: number;
    totalQuantity: number;
    ordersCount: number;
    savingsPercentLabel: string;
    avgSupplierPricePerUnit: number | null;
    lastRequestParamsPresent: boolean;
    canLoadMoreOrders: boolean;
    loadMoreOrders: () => void;
}

export const useReportsPageSavingsSummaryProduct =
    (): UseReportsPageSavingsSummaryProductResult => {
        const { sku } = useParams<{ sku: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();
        const lastRequestParams = useSelector(
            getReportSavingsLastRequestParams,
        );

        const locationState = location.state as {
            product?: ProductMetricsDto;
            fromMissedSavings?: boolean;
        } | null;
        const product = locationState?.product ?? null;
        const fromMissedSavings = locationState?.fromMissedSavings === true;

        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [ordersData, setOrdersData] =
            useState<ProductOrdersBySkuResponse | null>(null);

        const loadOrders = useCallback(
            async (page: number) => {
                if (!sku || !lastRequestParams) {
                    return;
                }

                setLoading(true);
                setError(null);

                try {
                    const requestBody: ProductsSkuRequestBody = {
                        dateFrom: lastRequestParams.dateFrom,
                        dateTo: lastRequestParams.dateTo,
                        propertyIds: lastRequestParams.propertyIds,
                        categoryIds: lastRequestParams.categoryIds,
                        orderBy: 'submitted_at',
                        page,
                        pageSize: 25,
                    };

                    const response =
                        await $api.post<ProductOrdersBySkuResponse>(
                            `/v1/reports/savings-insights/products/${sku}`,
                            requestBody,
                        );

                    setOrdersData((prev) => {
                        if (!prev || page === 1) {
                            return response.data;
                        }

                        return {
                            ...response.data,
                            items: [...prev.items, ...response.data.items],
                        };
                    });
                } catch {
                    setError('failed');
                } finally {
                    setLoading(false);
                }
            },
            [lastRequestParams, sku],
        );

        useEffect(() => {
            let isCancelled = false;

            const wrapped = async () => {
                if (isCancelled) return;
                await loadOrders(1);
            };

            wrapped();

            return () => {
                isCancelled = true;
            };
        }, [loadOrders]);

        const canLoadMoreOrders =
            Boolean(ordersData) &&
            ordersData!.page < ordersData!.totalPages &&
            !loading;

        const loadMoreOrders = useCallback(() => {
            if (!ordersData || ordersData.page >= ordersData.totalPages) {
                return;
            }
            void loadOrders(ordersData.page + 1);
        }, [loadOrders, ordersData]);

        const dateFrom = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateFrom)
                    : new Date(),
            [lastRequestParams],
        );
        const dateTo = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateTo)
                    : new Date(),
            [lastRequestParams],
        );

        const {
            totalSaved,
            totalSpent,
            baselineTotal,
            totalQuantity,
            ordersCount,
            savingsPercentLabel,
            avgSupplierPricePerUnit,
        } = useMemo(() => {
            const totalSavedNum =
                product?.totalSaved != null ? Number(product.totalSaved) : 0;
            const totalSpentNum =
                product?.totalPaid != null ? Number(product.totalPaid) : 0;
            const baseline = totalSavedNum + totalSpentNum;
            const quantity = product?.totalQty ?? 0;
            const orders = ordersData?.count ?? 0;
            const savingsPctLabel = product
                ? formatReportPercent(product.percentSaved ?? '0')
                : '';
            const avgPerUnit = quantity > 0 ? baseline / quantity : null;

            return {
                totalSaved: totalSavedNum,
                totalSpent: totalSpentNum,
                baselineTotal: baseline,
                totalQuantity: quantity,
                ordersCount: orders,
                savingsPercentLabel: savingsPctLabel,
                avgSupplierPricePerUnit: avgPerUnit,
            };
        }, [ordersData, product]);

        return {
            isSavingsSummaryVisible,
            sku,
            product,
            fromMissedSavings,
            loading,
            error,
            ordersData,
            dateFrom,
            dateTo,
            datePreset: lastRequestParams?.datePreset,
            totalSaved,
            totalSpent,
            baselineTotal,
            totalQuantity,
            ordersCount,
            savingsPercentLabel,
            avgSupplierPricePerUnit,
            lastRequestParamsPresent: Boolean(lastRequestParams),
            canLoadMoreOrders,
            loadMoreOrders,
        };
    };
